import type { CombatVisualEvent, DamageType } from '../../../shared/game/combat-types.js';

/** Presentation only. Every random choice is seeded by the event id, so a given blow always bleeds the same way. */
export const GRAVITY = 9.8;
const BLEEDS: ReadonlySet<DamageType> = new Set(['slashing', 'piercing', 'bludgeoning']);
/** A slash throws a wide fan, a puncture a narrow spurt that runs on with the weapon, a blunt blow a short spatter. */
export type Wound = 'slash' | 'puncture' | 'blunt';

export function mulberry32(seed: number) {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = Math.imul(state ^ state >>> 15, 1 | state);
    t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t;
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}

/** What kind of wound a visual event leaves, if it bleeds at all: fire, frost, lightning and the unseen do not. */
export function woundOf(event: CombatVisualEvent): Wound | undefined {
  if (event.kind !== 'impact' || event.damage <= 0) return undefined;
  const type = event.damageType ?? 'slashing';
  if (!BLEEDS.has(type)) return undefined;
  if (type === 'piercing') return 'puncture';
  if (type === 'bludgeoning') return 'blunt';
  return event.ranged ? 'puncture' : 'slash';
}

/** A critical slash that kills takes the head off. */
export const isDecapitation = (event: CombatVisualEvent) => !!event.lethal && event.critical && woundOf(event) === 'slash';

/** The part of a unit's own look a severed head needs, in world space. */
export interface SeveredHead {
  /** Where the neck was cut, and which way the head was pointing. */
  neck: { x: number; y: number; z: number }; up: { x: number; y: number; z: number };
  radius: number; skin: number; hair: number;
  /** Where the neck stump is now, in world space, once the body has moved (it falls): a fountain of blood follows it. Absent when the body is not tracked. */
  stump?: () => { x: number; y: number; z: number } | undefined;
}

export interface Droplet { x: number; y: number; z: number; vx: number; vy: number; vz: number; size: number; age: number; life: number; tone: number }
/** A stain on the ground. It spreads to `radius` over `grow` seconds after `delay`, then dries and fades. */
export interface Stain { x: number; z: number; radius: number; delay: number; grow: number; life: number; shade: number; seed: number }
/** A pulsing jet from a cut neck, leaning away from whoever struck. */
export interface Fountain { x: number; y: number; z: number; lx: number; lz: number; age: number; life: number; owed: number;
  /** Where the source is now. A fountain from a neck follows the stump as the body falls; without it the fountain only sinks at a fixed rate. */
  follow?: () => { x: number; y: number; z: number } | undefined }
export interface HeadBody { x: number; y: number; z: number; vx: number; vy: number; vz: number; spin: { x: number; y: number; z: number }; radius: number; bounces: number; resting: boolean; age: number; trail: number }

const unit = (x: number, z: number) => { const length = Math.hypot(x, z); return length < 1e-6 ? { x: 0, z: 1 } : { x: x / length, z: z / length }; };
/** Direction the blow travelled, from attacker to target, on the ground plane. */
export const blowDirection = (event: CombatVisualEvent) => unit(event.to.x - event.from.x, event.to.z - event.from.z);

/**
 * The droplets one wound throws. `strength` (0 to 1) is how much of the target's life the blow took, `height` the target's
 * height, `radius` its base. They leave the near side of the body at chest height, or at `y` for a wound elsewhere.
 */
export function woundDroplets(event: CombatVisualEvent, wound: Wound, strength: number, height: number, radius: number, rng: () => number, y = height * .62): Droplet[] {
  const d = blowDirection(event), side = rng() < .5 ? -1 : 1, heavy = event.critical ? 1.5 : 1;
  const count = Math.round((wound === 'slash' ? 16 + 30 * strength : wound === 'puncture' ? 9 + 14 * strength : 8 + 12 * strength) * heavy);
  // The wound is on the face of the body the blow came from.
  const ox = event.to.x - d.x * radius * .7, oz = event.to.z - d.z * radius * .7;
  const drops: Droplet[] = [];
  for (let i = 0; i < count; i++) {
    const t = count > 1 ? i / (count - 1) * 2 - 1 : 0;
    // A slash sweeps its fan across the body; a puncture holds a tight cone along the weapon's line; a blunt blow bursts wide.
    const spread = wound === 'slash' ? t * .95 + (rng() - .5) * .25 + side * .3 : wound === 'puncture' ? (rng() - .5) * .5 : (rng() - .5) * 3.2;
    const cos = Math.cos(spread), sin = Math.sin(spread);
    const dx = d.x * cos - d.z * sin, dz = d.x * sin + d.z * cos;
    const speed = (wound === 'slash' ? 3.2 + rng() * 4.2 : wound === 'puncture' ? 2.6 + rng() * 3.4 : 1.6 + rng() * 2.4) * (.75 + .5 * strength) * (event.critical ? 1.2 : 1);
    const up = wound === 'slash' ? 1 + rng() * 3.2 : wound === 'puncture' ? .6 + rng() * 2 : 1.5 + rng() * 2.6;
    drops.push({ x: ox, y: y + (rng() - .5) * height * .22, z: oz, vx: dx * speed, vy: up, vz: dz * speed, size: (.016 + rng() * .03) * (.85 + .5 * strength), age: 0, life: 1.4, tone: rng() });
  }
  // A puncture also weeps: slow drops that fall from the wound and pool beneath it.
  if (wound === 'puncture') for (let i = 0; i < 5; i++) drops.push({ x: ox, y: y - i * .03, z: oz, vx: (rng() - .5) * .35, vy: rng() * .5, vz: (rng() - .5) * .35, size: .02 + rng() * .02, age: -i * .12 - rng() * .1, life: 1.4, tone: .2 + rng() * .3 });
  return drops;
}

/**
 * A Sneak Attack opens the wound wide: a heavy burst of thick, dark gouts thrown along the blow and out to both sides, and a short
 * pulsing gush from the wound itself (a `Fountain` with nothing to follow, sinking as the body sags).
 */
export function sneakGore(event: CombatVisualEvent, strength: number, height: number, radius: number, rng: () => number): { drops: Droplet[]; gush: Fountain } {
  const d = blowDirection(event), y = height * .58;
  const ox = event.to.x - d.x * radius * .6, oz = event.to.z - d.z * radius * .6;
  const drops: Droplet[] = [];
  for (let i = 0; i < Math.round(22 + 18 * strength); i++) {
    // Most follow the blade through the body; a third spill back toward the striker.
    const back = rng() < .3, spread = (rng() - .5) * (back ? 1.6 : 2.2), cos = Math.cos(spread), sin = Math.sin(spread);
    const dx = (d.x * cos - d.z * sin) * (back ? -1 : 1), dz = (d.x * sin + d.z * cos) * (back ? -1 : 1), speed = 1.8 + rng() * (back ? 2.2 : 4.6);
    const gout = rng() < .35;
    drops.push({ x: ox, y: y + (rng() - .5) * height * .16, z: oz, vx: dx * speed, vy: .6 + rng() * 2.8, vz: dz * speed,
      size: gout ? .032 + rng() * .026 : .016 + rng() * .024, age: -rng() * .08, life: gout ? 1.1 : 1.5, tone: gout ? .75 + rng() * .25 : rng() * .6 });
  }
  return { drops, gush: { x: ox, y, z: oz, lx: d.x * .55, lz: d.z * .55, age: 0, life: .5, owed: 0 } };
}

/** Droplets from one moment of a neck jet. Pulses with the heart: strong spurts with lulls between, dwindling as it empties. */
export function fountainDroplets(fountain: Fountain, dt: number, rng: () => number): Droplet[] {
  const t = fountain.age / fountain.life, pulse = Math.max(0, Math.sin(fountain.age * 13)) ** 1.5, rate = 170 * pulse * (1 - t) ** 1.3 + 25 * (1 - t);
  fountain.owed += rate * dt;
  const drops: Droplet[] = [];
  while (fountain.owed >= 1) {
    fountain.owed -= 1;
    const angle = rng() * Math.PI * 2, scatter = rng() * .9;
    drops.push({ x: fountain.x, y: fountain.y, z: fountain.z, vx: fountain.lx * (1.3 + rng() * 1.5) + Math.cos(angle) * scatter, vy: (2.6 + rng() * 3.4) * (.55 + .45 * (1 - t)), vz: fountain.lz * (1.3 + rng() * 1.5) + Math.sin(angle) * scatter, size: .018 + rng() * .03, age: 0, life: 1.6, tone: rng() * .6 });
  }
  return drops;
}

/** The head leaves at the neck: carried by the blow, thrown up, tumbling. */
export function launchHead(event: CombatVisualEvent, head: SeveredHead, rng: () => number): HeadBody {
  const d = blowDirection(event), power = 2.2 + rng() * 1.6;
  return {
    x: head.neck.x + head.up.x * head.radius, y: head.neck.y + head.up.y * head.radius, z: head.neck.z + head.up.z * head.radius,
    vx: d.x * power + (rng() - .5) * 1.4, vy: 3.4 + rng() * 1.8, vz: d.z * power + (rng() - .5) * 1.4,
    spin: { x: (rng() - .5) * 18, y: (rng() - .5) * 12, z: (rng() - .5) * 18 }, radius: head.radius, bounces: 0, resting: false, age: 0, trail: 0,
  };
}

/** Where a droplet that reaches the ground leaves a stain, if it does (small ones just vanish). */
export function stainFrom(drop: Droplet, rng: () => number): Stain | undefined {
  if (drop.size < .022 || rng() > .55) return undefined;
  return { x: drop.x, z: drop.z, radius: drop.size * (2.2 + rng() * 2), delay: 0, grow: .12, life: 36, shade: rng(), seed: Math.floor(rng() * 1000) };
}
/** The slow pool that spreads under whoever dies. Larger for a heavier blow and a bigger body. */
export function poolFor(event: CombatVisualEvent, strength: number, radius: number, rng: () => number, decapitated = false): Stain {
  return { x: event.to.x, z: event.to.z, radius: radius * (.9 + .8 * strength + (decapitated ? .5 : 0)) * (.9 + rng() * .25), delay: .9, grow: decapitated ? 5.5 : 4, life: 80, shade: .1, seed: Math.floor(rng() * 1000) };
}

const GROUND = 0;
/** Advances droplets by `dt` seconds. Those that reach the ground (or run out) are handed to `landed` and dropped from the list. */
export function stepDroplets(list: Droplet[], dt: number, landed: (drop: Droplet) => void, groundHeight: (x: number, z: number) => number = () => GROUND): Droplet[] {
  const alive: Droplet[] = [];
  for (const drop of list) {
    drop.age += dt;
    if (drop.age < 0) { alive.push(drop); continue; } // not yet shed
    drop.vy -= GRAVITY * dt; drop.vx *= 1 - .6 * dt; drop.vz *= 1 - .6 * dt;
    drop.x += drop.vx * dt; drop.y += drop.vy * dt; drop.z += drop.vz * dt;
    const floor = groundHeight(drop.x, drop.z);
    if (drop.y <= floor + .02) { drop.y = floor + .02; landed(drop); continue; }
    if (drop.age < drop.life) alive.push(drop);
  }
  return alive;
}

/** Tumbles a head under gravity. `bounced` fires on each bounce with how hard it hit; it comes to rest rolling to a stop. */
export function stepHead(head: HeadBody, dt: number, bounced: (hardness: number) => void, groundHeight: (x: number, z: number) => number = () => GROUND) {
  head.age += dt;
  if (head.resting) return;
  head.vy -= GRAVITY * dt;
  head.x += head.vx * dt; head.y += head.vy * dt; head.z += head.vz * dt;
  const floor = groundHeight(head.x, head.z);
  if (head.y <= floor + head.radius && head.vy < 0) {
    head.y = floor + head.radius;
    const hardness = -head.vy;
    head.bounces++;
    // Each strike eats most of the fall; the head keeps rolling along the ground.
    head.vy = hardness > 1.1 ? hardness * .36 : 0;
    const grip = Math.exp(-3 * dt) * .75;
    head.vx *= grip; head.vz *= grip; head.spin.x *= .62; head.spin.y *= .62; head.spin.z *= .62;
    if (hardness > .6) bounced(hardness);
    if (head.vy === 0 && Math.hypot(head.vx, head.vz) < .35) { head.resting = true; head.vx = head.vz = 0; head.spin = { x: 0, y: 0, z: 0 }; }
  } else if (head.y <= floor + head.radius) {
    // Rolling.
    head.y = floor + head.radius; const roll = Math.exp(-3.2 * dt); head.vx *= roll; head.vz *= roll; head.spin.x *= roll; head.spin.z *= roll;
    if (Math.hypot(head.vx, head.vz) < .2) { head.resting = true; head.vx = head.vz = 0; head.spin = { x: 0, y: 0, z: 0 }; }
  }
}
